import { CSSProperties, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { Checkbox } from '@mui/material';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { formatNumbers, getObjectValue } from 'utilities';
import { NumberMultipleCheckboxProps } from './NumberMultipleCheckbox.props';
import useNumberMultipleCheckboxStyles from './NumberMultipleCheckbox.styles';
import Strike from './Strike';

// isInvalidForCheckbox basically makes the checkbox a no-op for a given cell
// for most cases it's about the numeric value being <= 0, but for investment type companies it also includes "N/A" and infinity
// and for backsolve market adjustment 0 and negative are allowed
const NumberMultipleCheckbox = ({
  cell,
  selected,
  rightAlignContent,
  toggleOnLabelClick,
}: NumberMultipleCheckboxProps) => {
  const labelTogglesCheckbox = toggleOnLabelClick !== false;
  const {
    allowNegativeValue: cellAllowNegativeValue,
    blankIfNull: cellBlankIfNull,
    currency: cellCurrencyRaw,
    currencyCode: cellCurrencyCode,
    data: cellData,
    enabled: cellEnabled,
    expr: cellExpr,
    format: cellFormat,
    isEditableTitleCell,
    prefix: cellPrefix,
    staticCurrency: cellStaticCurrency,
    suffix: cellSuffix,
    value: cellValue,
  } = cell;

  const cellCurrency = cellCurrencyRaw ?? (cell as any).customFormat?.currency;

  const { isInvalidForCheckbox } = getObjectValue(cellData);

  const classes = useNumberMultipleCheckboxStyles({ isEditableTitleCell, rightAlignContent });
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  // The cell owns the include/exclude state. Reading it straight off the cell keeps this
  // checkbox correct when something else flips it (bulk selection actions, another sheet).
  const enabled = cellEnabled;
  const [, repaint] = useReducer((tick: number) => tick + 1, 0);

  const changeCell = useCallback(
    (enable: boolean) => {
      cell.setProps?.({ enabled: enable, ignoreExpr: !enable });
      repaint();
      onCellsChanged([{ cell, value: cellExpr }]);
    },
    [cell, cellExpr, onCellsChanged]
  );

  // Always flip the cell's current state, never a value captured at render: a bulk
  // action can have changed it since, and toggling a stale copy is a no-op.
  const toggleCell = useCallback(() => changeCell(!cell.enabled), [cell, changeCell]);

  // The cell announces its own changes; repaint on them so an external toggle shows here too.
  useEffect(() => {
    const cellEventKey = cell.getFullKey?.();
    if (!cellEventKey) {
      return undefined;
    }
    document.addEventListener(cellEventKey, repaint);
    return () => document.removeEventListener(cellEventKey, repaint);
  }, [cell]);

  // Function to check if the target is a table cell container or a checkbox
  const isValidTarget = useCallback((target: HTMLInputElement) => {
    const isDataGridContainer = target.tagName === 'SPAN' && target.classList.contains('data-grid-container');
    const isInputCheckbox = target.tagName === 'INPUT' && target.type === 'checkbox';
    return isDataGridContainer || isInputCheckbox;
  }, []);

  const keyUpDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const { code, type, target } = event;

      // If the target is valid
      if (isValidTarget(target as HTMLInputElement)) {
        if (selected && code === 'Space') {
          // Prevent the default spacebar behavior (scroll down)
          event.preventDefault();
          if (type === 'keyup' && !isInvalidForCheckbox) {
            toggleCell();
          }
        }
      } else {
        // Remove the keyup event to prevent the spacebar from being captured by the component elsewhere
        document.removeEventListener('keyup', keyUpDownHandler);
      }
    },
    [isValidTarget, selected, isInvalidForCheckbox, toggleCell]
  );

  // Click handler to check if the user clicked outside a cell
  const handleClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      // If the click did not occur inside a table cell ('td'), remove the keyup event
      if (!target?.closest('td')) {
        document.removeEventListener('keyup', keyUpDownHandler);
      }
    },
    [keyUpDownHandler]
  );

  useEffect(() => {
    if (!selected) return;
    document.addEventListener('keyup', keyUpDownHandler);
    document.addEventListener('keydown', keyUpDownHandler);
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('keyup', keyUpDownHandler);
      document.removeEventListener('keydown', keyUpDownHandler);
      document.removeEventListener('click', handleClick);
    };
  }, [selected, keyUpDownHandler, handleClick]);

  // A click on the checkbox is only ever a toggle: it must not reach the datasheet
  // underneath, which would start a drag selection or open the cell editor.
  const stopMouseDown = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  // Valuations "multiples": clicking (or pressing Enter/Space on) the number/label
  // toggles include/exclude. The label only renders this interactive form while the
  // cell is selected and label-toggling is enabled, so it fires as the original
  // "second click toggles" gesture. stopPropagation keeps it from bubbling to the
  // datasheet (the cell is already selected). An invalid number is never toggled, so
  // the gesture falls through to the datasheet instead.
  const handleLabelClick = useCallback(
    (e: React.MouseEvent) => {
      if (isInvalidForCheckbox) return;
      e.stopPropagation();
      toggleCell();
    },
    [isInvalidForCheckbox, toggleCell]
  );

  const handleLabelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isInvalidForCheckbox || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      e.stopPropagation();
      toggleCell();
    },
    [isInvalidForCheckbox, toggleCell]
  );

  const CellValueFormatted = useMemo(
    () =>
      formatNumbers({
        format: cellFormat,
        prefix: cellPrefix ?? null,
        suffix: cellSuffix,
        allowNegativeValue: cellAllowNegativeValue,
        currency: cellCurrency,
        currencyCode: cellCurrencyCode,
        blankIfNull: cellBlankIfNull,
        staticCurrency: cellStaticCurrency,
        value: cellValue,
      }),

    [
      cellAllowNegativeValue,
      cellBlankIfNull,
      cellCurrency,
      cellCurrencyCode,
      cellFormat,
      cellPrefix,
      cellStaticCurrency,
      cellSuffix,
      cellValue,
    ]
  );

  const numberDisplay = useMemo(
    () => (
      <Strike classes={classes} enabled={enabled} isValidNumber={!isInvalidForCheckbox} value={CellValueFormatted} />
    ),
    [CellValueFormatted, enabled, classes, isInvalidForCheckbox]
  );

  const wrappingDivStyle: CSSProperties | undefined =
    isEditableTitleCell && !rightAlignContent ? { textAlign: 'left' } : undefined;

  // Manual layout - intentionally NOT wrapped in MUI FormControlLabel, which binds
  // label clicks to the checkbox and made every click on the amount (double-click
  // included) toggle the exclusion. As siblings, only the checkbox itself toggles;
  // everything else bubbles to the datasheet for selection / edit. Valuations
  // multiples additionally make the label clickable; CFL turns that off
  // (toggleOnLabelClick=false) so double-click-to-edit survives.
  //
  // The row and the label span are rendered in every state on purpose. Swapping
  // them out when the cell gets selected detaches the node the mousedown started
  // on, and the datasheet then reads the gesture as a click outside itself and
  // cancels the drag - so a selection could not start on one of these cells.
  // While it is clickable the label owns its own click, so the datasheet leaves the gesture alone.
  const labelInteraction = labelTogglesCheckbox && selected
    ? {
      'data-selection-passthrough': 'true',
      onClick: handleLabelClick,
      onKeyDown: handleLabelKeyDown,
      role: 'button',
      tabIndex: 0,
      style: { cursor: 'pointer' },
    }
    : {};
  const content = (
    <div className={classes.controlLabelRoot}>
      <span
        className={selected ? classes.label : `${classes.label} unselected-checkbox-cell`}
        {...labelInteraction}>
        {isInvalidForCheckbox && !selected ? '-' : numberDisplay}
      </span>
      {selected &&
        (isInvalidForCheckbox ? (
          <span style={{ paddingRight: '2.2rem' }} />
        ) : (
          <Checkbox
            checked={Boolean(enabled)}
            data-selection-passthrough="true"
            onChange={toggleCell}
            onMouseDown={stopMouseDown}
          />
        ))}
    </div>
  );
  return <div style={wrappingDivStyle}>{content}</div>;
};

export default NumberMultipleCheckbox;
